/**
 * Treatment Plan Sessions API service for persistent wizard state
 */

import authService from "./auth";
import {
  TreatmentPlanSession,
  TreatmentPlanSessionListItem,
  ClientSuggestion,
  DocumentAnalysisResponse,
  InputPanelState,
  SectionGenerationState,
} from "../types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Request types
export interface CreateSessionRequest {
  client_name: string;
  date_of_birth?: string | null;
}

export interface UpdateSessionRequest {
  client_name?: string;
  date_of_birth?: string | null;
  status?: string;
  current_step?: number;
  analyzed_documents?: Record<string, DocumentAnalysisResponse | null>;
  generated_sections?: Record<string, any>;
  section_states?: Record<string, SectionGenerationState>;
  input_states?: Record<string, InputPanelState>;
}

export interface StateUpdateRequest {
  analyzed_documents?: Record<string, DocumentAnalysisResponse | null>;
  generated_sections?: Record<string, any>;
  section_states?: Record<string, SectionGenerationState>;
  input_states?: Record<string, InputPanelState>;
}

/**
 * List all treatment plan sessions for the current user
 */
export async function listSessions(
  clientName?: string,
  status?: string
): Promise<TreatmentPlanSessionListItem[]> {
  const params = new URLSearchParams();
  if (clientName) params.append("client_name", clientName);
  if (status) params.append("status", status);

  const queryString = params.toString();
  const url = `${API_BASE_URL}/api/treatment-plan-sessions/${queryString ? `?${queryString}` : ""}`;

  const response = await authService.authenticatedFetch(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to list sessions");
  }

  return response.json();
}

/**
 * Get client suggestions for auto-complete
 */
export async function suggestClients(
  query?: string
): Promise<ClientSuggestion[]> {
  const params = new URLSearchParams();
  if (query) params.append("query", query);

  const queryString = params.toString();
  const url = `${API_BASE_URL}/api/treatment-plan-sessions/clients/suggest${queryString ? `?${queryString}` : ""}`;

  const response = await authService.authenticatedFetch(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get client suggestions");
  }

  return response.json();
}

/**
 * Create a new treatment plan session
 */
export async function createSession(
  data: CreateSessionRequest
): Promise<TreatmentPlanSession> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create session");
  }

  return response.json();
}

/**
 * Get a specific session with full state
 */
export async function getSession(
  sessionId: number
): Promise<TreatmentPlanSession> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get session");
  }

  return response.json();
}

/**
 * Update a session (full update)
 */
export async function updateSession(
  sessionId: number,
  data: UpdateSessionRequest
): Promise<TreatmentPlanSession> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update session");
  }

  return response.json();
}

/**
 * Update session state (partial merge for auto-save)
 */
export async function updateSessionState(
  sessionId: number,
  data: StateUpdateRequest
): Promise<TreatmentPlanSession> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}/state`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update session state");
  }

  return response.json();
}

/**
 * Update the current wizard step
 */
export async function updateSessionStep(
  sessionId: number,
  currentStep: number
): Promise<TreatmentPlanSession> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}/step`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ current_step: currentStep }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update session step");
  }

  return response.json();
}

/**
 * Mark a session as completed
 */
export async function markSessionComplete(
  sessionId: number
): Promise<TreatmentPlanSession> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}/complete`,
    {
      method: "PATCH",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to mark session complete");
  }

  return response.json();
}

/**
 * Delete a session
 */
export async function deleteSession(sessionId: number): Promise<void> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}`,
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to delete session");
  }
}

/**
 * Clear a document from a session (removes uploaded file, analysis results, and S3 object)
 */
export async function clearSessionDocument(
  sessionId: number,
  documentType: string
): Promise<void> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}/files/${documentType}`,
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to clear document from session");
  }
}

export interface FilePreviewUrlResponse {
  url: string;
  filename: string;
  mime_type: string;
}

/**
 * Get a presigned URL for previewing an uploaded file from S3.
 * Works for PDFs which browsers can render natively in iframes.
 */
export async function getFilePreviewUrl(
  sessionId: number,
  documentType: string
): Promise<FilePreviewUrlResponse> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}/files/${documentType}/url`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get file preview URL");
  }

  return response.json();
}

export interface FilePreviewPdfResponse {
  pdf_data: string | null;
  error: string | null;
}

/**
 * Get a PDF preview of an uploaded file from S3.
 * The backend downloads the file, converts Excel to PDF, and returns base64 data.
 * Use this for Excel files that browsers can't render natively.
 */
export async function getFilePreviewPdf(
  sessionId: number,
  documentType: string
): Promise<FilePreviewPdfResponse> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-plan-sessions/${sessionId}/files/${documentType}/preview`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get file preview");
  }

  return response.json();
}
